import { useState } from 'react'
import { supabase } from '../../api/supabase'
import type { Invoice } from '../../hooks/useInvoices'
import { VOID_REASONS } from '../../lib/invoiceRules'
import { fmt } from '../../lib/invoiceFormat'
import InvoiceDialogShell, { ErrorBox, btnCancel, fieldCls, labelCls } from './InvoiceDialogShell'

export default function VoidInvoiceDialog({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    const { error } = await supabase.rpc('void_invoice', { p_invoice_id: invoice.id, p_reason: reason, p_notes: notes.trim() || null })
    setBusy(false)
    if (error) { setError(error.message); return }
    onDone()
  }

  return (
    <InvoiceDialogShell title={`⚠️ Void invoice ${invoice.invoice_no} (${fmt(invoice.total_amount || 0, invoice.currency)})`} tone="danger"
      footer={<>
        <button onClick={onClose} className={btnCancel}>Cancel</button>
        <button onClick={submit} disabled={!reason || busy} className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
          {busy ? 'Voiding…' : 'Confirm Void'}
        </button>
      </>}>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        A voided invoice no longer counts towards receivables or the VAT return.
      </p>
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        If this invoice has already been sent to the customer, raise a <b>Credit Note</b> instead of voiding it.
        Void is only for an invoice that was never sent.
      </p>
      <div>
        <label className={labelCls}>Void reason *</label>
        <select value={reason} onChange={e => setReason(e.target.value)} className={fieldCls}>
          <option value="">Select a reason…</option>
          {VOID_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>Notes (optional)</label>
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className={fieldCls} />
      </div>
      <ErrorBox message={error} />
    </InvoiceDialogShell>
  )
}
